import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '@savgroup-front-common/primitive/lib/utils';

export const buttonVariants = cva(
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring-base-default focus-visible:ring-3 focus-visible:ring-ring-base-default active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-border-danger-default aria-invalid:ring-3 aria-invalid:ring-border-danger-default/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      variant: {
        default:
          'bg-surface-primary-default stext-inverse-default hover:bg-surface-primary-hover active:bg-surface-primary-active',
        outline:
          'border-border-base-default bg-surface-base-default stext-base-default shadow-xs hover:bg-surface-base-hover aria-expanded:bg-surface-base-hover',
        'outline-primary':
          'sborder-primary-default bg-surface-base-default stext-primary-default shadow-xs hover:bg-surface-primary-subtle-hover active:bg-surface-primary-subtle-active aria-expanded:bg-surface-primary-subtle-hover',
        secondary:
          'bg-surface-secondary-default stext-inverse-default hover:bg-surface-secondary-hover active:bg-surface-secondary-active aria-expanded:bg-surface-secondary-hover',
        ghost:
          'stext-base-default hover:bg-surface-base-hover aria-expanded:bg-surface-base-hover',
        'ghost-primary':
          'stext-primary-default hover:bg-surface-primary-subtle-hover active:bg-surface-primary-subtle-active aria-expanded:bg-surface-primary-subtle-hover',
        menu: 'stext-contrast-default hover:bg-white/10 hover:stext-contrast-default aria-expanded:bg-white/10 aria-expanded:stext-contrast-default',
        destructive:
          'bg-surface-danger-default stext-inverse-default hover:bg-surface-danger-hover active:bg-surface-danger-active',
        'ghost-destructive':
          'stext-danger-default hover:bg-surface-danger-subtle-default active:bg-surface-danger-subtle-default aria-expanded:bg-surface-danger-subtle-default',
        link: 'stext-link-default underline-offset-4 hover:underline',
      },
      size: {
        default:
          'h-9 gap-1.5 px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1 rounded-[min(var(--radius-md),10px)] px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
        lg: 'h-10 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        icon: 'size-9',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm':
          'size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-md',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(
        'revers-button',
        buttonVariants({ variant, size, className }),
      )}
      {...props}
    />
  );
}
